import React, { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  BadgeCheck,
  Banknote,
  Check,
  Loader2,
  Package,
  Palette,
  Plus,
  RefreshCw,
  Settings,
  Shield,
  Store,
  Trash2,
  X,
} from "lucide-react";
import MarketplacePreviewCard from "../components/MarketplacePreviewCard";
import ServerCardDesigner, { EMPTY_CREATOR_PRODUCT } from "../components/ServerCardDesigner";

const API_ORIGIN = window.location.hostname === "localhost" ? "https://disparty.com" : "";

async function api(path, options = {}) {
  const response = await fetch(`${API_ORIGIN}${path}`, {
    credentials: "include",
    ...options,
    headers: options.body instanceof FormData
      ? options.headers
      : { "Content-Type": "application/json", ...(options.headers || {}) },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(data.error || data.message || "요청을 처리하지 못했습니다.");
  return data;
}

function money(value) {
  return `${Number(value || 0).toLocaleString()}원`;
}

function shortDate(value) {
  if (!value) return "-";
  return new Intl.DateTimeFormat("ko-KR", { dateStyle: "medium", timeStyle: "short" }).format(new Date(value));
}

const STATUS_LABELS = {
  PENDING: "검토 중",
  APPROVED: "승인됨",
  REJECTED: "반려됨",
  SUSPENDED: "이용 정지",
  DRAFT: "초안",
  PUBLISHED: "판매 중",
  HIDDEN: "판매 중지",
  NONE: "저장됨",
  REQUESTED: "접수",
  PROCESSING: "처리 중",
  PAID: "지급 완료",
  REFUNDED: "환불",
};

function StatusBadge({ status }) {
  const safe = status || "NONE";
  const tone = ["APPROVED", "PUBLISHED", "PAID"].includes(safe)
    ? "bg-emerald-500/15 text-emerald-300"
    : ["REJECTED", "SUSPENDED"].includes(safe)
      ? "bg-red-500/15 text-red-300"
      : ["PENDING", "REQUESTED", "PROCESSING"].includes(safe)
        ? "bg-amber-500/15 text-amber-200"
        : "bg-white/10 text-gray-300";
  return <span className={`rounded-md px-2 py-1 text-[11px] font-bold ${tone}`}>{STATUS_LABELS[safe] || safe}</span>;
}

function Notice({ message, error }) {
  if (!message) return null;
  return <div className={`fixed right-4 top-16 z-[1500] max-w-sm rounded-lg border px-4 py-3 text-sm shadow-2xl ${error ? "border-red-400/30 bg-red-950 text-red-100" : "border-emerald-400/30 bg-emerald-950 text-emerald-100"}`}>{message}</div>;
}

function SellerApplication({ seller, settings, onSaved, isAdmin, onAdmin, embedded = false }) {
  const reduceMotion = useReducedMotion();
  const [form, setForm] = useState({
    displayName: seller?.displayName || "",
    bio: seller?.bio || "",
    sellerType: seller?.sellerType || "INDIVIDUAL",
    legalName: seller?.legalName || "",
    businessName: seller?.businessName || "",
    businessRegistrationNumber: seller?.businessRegistrationNumber || "",
    address: seller?.address || "",
    email: seller?.email || "",
    phone: seller?.phone || "",
    bankName: seller?.bankName || "",
    accountHolder: seller?.accountHolder || "",
    accountNumber: "",
    acceptTerms: false,
    acceptPrivacy: false,
    confirmAdult: false,
    confirmAccurateInfo: false,
  });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState("");
  const [nameCheck, setNameCheck] = useState({ checking: false, available: null, message: "" });
  const set = (field, value) => setForm((current) => ({ ...current, [field]: value }));
  const nameChangesRemaining = seller ? Number(seller.displayNameChangesRemaining ?? 2) : 2;
  const nameChangeLocked = Boolean(seller && nameChangesRemaining <= 0);
  const commissionPercent = Number(settings.commissionRateBps ?? 3000) / 100;
  const sellerRevenuePercent = 100 - commissionPercent;

  useEffect(() => {
    const name = form.displayName.trim();
    if (!name) {
      setNameCheck({ checking: false, available: null, message: "" });
      return undefined;
    }
    if (seller && name === seller.displayName) {
      setNameCheck({ checking: false, available: true, message: `현재 이름 · 이번 달 ${nameChangesRemaining}회 변경 가능` });
      return undefined;
    }
    if (nameChangeLocked) {
      setNameCheck({ checking: false, available: false, message: "이번 달 이름 변경 횟수 2회를 모두 사용했습니다." });
      return undefined;
    }
    setNameCheck({ checking: true, available: null, message: "확인 중" });
    const timer = window.setTimeout(() => {
      api(`/api/creator/name-availability?name=${encodeURIComponent(name)}`)
        .then((result) => setNameCheck({
          checking: false,
          available: Boolean(result.available),
          message: result.available ? `사용 가능 · 변경 후 이번 달 ${Math.max(0, nameChangesRemaining - 1)}회 남음` : "이미 사용 중인 이름입니다.",
        }))
        .catch((error) => setNameCheck({ checking: false, available: false, message: error.message }));
    }, 350);
    return () => window.clearTimeout(timer);
  }, [form.displayName, nameChangeLocked, nameChangesRemaining, seller]);

  const submit = async (event) => {
    event.preventDefault();
    if (nameCheck.checking || nameCheck.available !== true) {
      setMessage("사용 가능한 크리에이터명인지 확인해주세요.");
      return;
    }
    setBusy(true);
    setMessage("");
    try {
      const saved = await api("/api/creator/apply", { method: "POST", body: JSON.stringify(form) });
      onSaved(saved);
    } catch (requestError) {
      setMessage(requestError.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`${embedded ? "bg-transparent px-4 py-10" : "min-h-screen bg-[#111216] px-4 py-20"} text-white`}>
      <div className="mx-auto max-w-3xl">
        <div className="mb-8 flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-sm font-bold text-[#9ea6ff]"><Store className="h-5 w-5" /> Disparty Creator</div>
            <h1 className="mt-3 text-3xl font-black">{seller ? "크리에이터 설정" : "서버 카드 판매 정보 등록"}</h1>
            <p className="mt-2 text-sm leading-6 text-gray-400">판매 대금의 {sellerRevenuePercent}%가 판매자 수익으로 적립되고, 구매일로부터 {settings.refundHoldDays || 7}일 뒤 정산 가능 금액으로 전환됩니다.</p>
          </div>
          {isAdmin && <button type="button" onClick={onAdmin} className="rounded-lg border border-[#4b4e58] bg-[#25262b] px-4 py-2 text-sm font-bold"><Shield className="mr-2 inline h-4 w-4" />관리자</button>}
        </div>

        {seller?.status === "SUSPENDED" && (
          <div className="mb-6 rounded-lg border border-[#3b3e47] bg-[#1c1d22] p-4">
            <div className="flex items-center gap-3"><StatusBadge status={seller.status} /><strong>판매가 정지된 계정입니다.</strong></div>
            {seller.statusReason && <p className="mt-2 text-sm text-red-300">{seller.statusReason}</p>}
          </div>
        )}

        <motion.form initial={reduceMotion ? false : { opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: reduceMotion ? 0 : 0.24, ease: "easeOut" }} onSubmit={submit} className="space-y-7 rounded-2xl border border-white/10 bg-white/5 p-5 shadow-xl backdrop-blur-sm sm:p-7">
          <section>
            <h2 className="mb-4 text-lg font-black">상점 정보</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <StudioField label={seller ? `크리에이터명 · 이번 달 ${nameChangesRemaining}/2회 남음` : "크리에이터명"}><input required disabled={nameChangeLocked} maxLength="40" value={form.displayName} onChange={(event) => set("displayName", event.target.value)} className="creator-input disabled:cursor-not-allowed disabled:opacity-60" /><span className={`mt-1.5 block text-xs ${nameCheck.available === true ? "text-emerald-300" : nameCheck.available === false ? "text-red-300" : "text-gray-500"}`}>{nameCheck.message}</span></StudioField>
              <StudioField label="판매자 유형"><div className="grid grid-cols-2 gap-2">{[["INDIVIDUAL", "개인"], ["BUSINESS", "사업자"]].map(([value, label]) => <button key={value} type="button" onClick={() => set("sellerType", value)} className={`h-11 rounded-lg border text-sm font-bold transition-all ${form.sellerType === value ? "border-indigo-500 bg-indigo-600 text-white shadow-lg shadow-indigo-600/20" : "border-white/10 bg-white/5 text-gray-300 hover:bg-white/10"}`}>{label}</button>)}</div></StudioField>
            </div>
            <div className="mt-4"><StudioField label="상점 소개"><textarea rows="3" maxLength="300" value={form.bio} onChange={(event) => set("bio", event.target.value)} className="creator-input resize-none" /></StudioField></div>
          </section>
          <section className="border-t border-[#30323a] pt-6">
            <h2 className="mb-4 text-lg font-black">본인 확인 및 연락처</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <StudioField label="실명"><input required value={form.legalName} onChange={(event) => set("legalName", event.target.value)} className="creator-input" /></StudioField>
              <StudioField label="휴대전화"><input required value={form.phone} onChange={(event) => set("phone", event.target.value)} className="creator-input" placeholder="숫자만 입력" /></StudioField>
              <StudioField label="이메일"><input required type="email" value={form.email} onChange={(event) => set("email", event.target.value)} className="creator-input" /></StudioField>
              <StudioField label="주소"><input required value={form.address} onChange={(event) => set("address", event.target.value)} className="creator-input" /></StudioField>
              {form.sellerType === "BUSINESS" && <><StudioField label="사업자명"><input required value={form.businessName} onChange={(event) => set("businessName", event.target.value)} className="creator-input" /></StudioField><StudioField label="사업자등록번호"><input required value={form.businessRegistrationNumber} onChange={(event) => set("businessRegistrationNumber", event.target.value)} className="creator-input" placeholder="10자리 숫자" /></StudioField></>}
            </div>
          </section>
          <section className="border-t border-[#30323a] pt-6">
            <h2 className="mb-1 text-lg font-black">정산 계좌</h2>
            <p className="mb-4 text-xs text-gray-500">계좌번호는 암호화되어 저장되며 관리자 정산 처리 화면에서만 복호화됩니다.</p>
            <div className="grid gap-4 sm:grid-cols-3">
              <StudioField label="은행"><input required value={form.bankName} onChange={(event) => set("bankName", event.target.value)} className="creator-input" /></StudioField>
              <StudioField label="예금주"><input required value={form.accountHolder} onChange={(event) => set("accountHolder", event.target.value)} className="creator-input" /></StudioField>
              <StudioField label={seller ? `계좌번호 · 현재 끝 ${seller.accountLast4}` : "계좌번호"}><input required={!seller} value={form.accountNumber} onChange={(event) => set("accountNumber", event.target.value)} className="creator-input" placeholder={seller ? "변경할 때만 입력" : "숫자만 입력"} /></StudioField>
            </div>
          </section>
          {seller && seller.termsVersion !== settings.sellerTermsVersion && (
            <div className="rounded-lg border border-amber-400/20 bg-amber-400/10 p-4 text-sm leading-6 text-amber-100">
              판매 수수료와 정산·환불 조건이 반영된 새 판매자 약관에 동의해야 판매를 계속할 수 있습니다.
            </div>
          )}
          <section className="space-y-3 border-t border-[#30323a] pt-6">
            <h2 className="text-lg font-black">필수 확인 및 동의</h2>
            <label className="flex cursor-pointer items-start gap-3 rounded-lg bg-black/20 p-4 text-sm leading-6 text-gray-300">
              <input required type="checkbox" checked={form.acceptTerms} onChange={(event) => set("acceptTerms", event.target.checked)} className="mt-1 accent-[#5865F2]" />
              <span>
                <Link to="/ko/seller-terms" target="_blank" className="text-[#aeb5ff] underline underline-offset-4">판매자 이용약관</Link>에 동의합니다. 판매 수수료 {commissionPercent}%, 환불 보류 기간 {settings.refundHoldDays || 7}일, 최소 정산 금액 {money(settings.minimumPayoutAmount || 50000)}이 적용됩니다.
                <small className="block text-gray-500">약관 버전 {settings.sellerTermsVersion}</small>
              </span>
            </label>
            <label className="flex cursor-pointer items-start gap-3 rounded-lg bg-black/20 p-4 text-sm leading-6 text-gray-300">
              <input required type="checkbox" checked={form.acceptPrivacy} onChange={(event) => set("acceptPrivacy", event.target.checked)} className="mt-1 accent-[#5865F2]" />
              <span>
                판매·정산을 위해 실명, 연락처, 주소, 사업자 정보 및 정산 계좌를 수집·이용하는 데 동의합니다. 자세한 내용은 <Link to="/ko/privacy" target="_blank" className="text-[#aeb5ff] underline underline-offset-4">개인정보 처리방침</Link>에서 확인할 수 있습니다.
              </span>
            </label>
            <label className="flex cursor-pointer items-start gap-3 rounded-lg bg-black/20 p-4 text-sm leading-6 text-gray-300">
              <input required type="checkbox" checked={form.confirmAdult} onChange={(event) => set("confirmAdult", event.target.checked)} className="mt-1 accent-[#5865F2]" />
              <span>만 19세 이상이며 판매 및 정산 계약을 체결할 권한이 있음을 확인합니다.</span>
            </label>
            <label className="flex cursor-pointer items-start gap-3 rounded-lg bg-black/20 p-4 text-sm leading-6 text-gray-300">
              <input required type="checkbox" checked={form.confirmAccurateInfo} onChange={(event) => set("confirmAccurateInfo", event.target.checked)} className="mt-1 accent-[#5865F2]" />
              <span>등록 정보와 정산 계좌가 본인 또는 해당 사업자의 정보와 일치하며, 판매 상품에 필요한 권리를 보유하고 있음을 확인합니다.</span>
            </label>
          </section>
          {message && <p className="text-sm text-red-300">{message}</p>}
          <button disabled={busy || nameCheck.checking || nameCheck.available !== true} className="flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-[#5865F2] font-bold hover:bg-[#4f5bd5] disabled:cursor-not-allowed disabled:opacity-50">{busy && <Loader2 className="h-4 w-4 animate-spin" />}{seller ? "판매 정보 저장" : "등록하고 판매 시작"}</button>
        </motion.form>
      </div>
    </div>
  );
}

function StudioField({ label, children }) {
  return <label className="block"><span className="mb-2 block text-xs font-bold text-gray-300">{label}</span>{children}</label>;
}

function ProductManager({ items, onEdit, onNew, onDelete, onSubmit, busy }) {
  const reduceMotion = useReducedMotion();
  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6">
      <div className="flex items-center justify-between"><div><h2 className="text-2xl font-black text-white">내 상품</h2><p className="mt-1 text-sm text-gray-500">판매 중인 버전은 새 수정본이 승인될 때까지 유지됩니다.</p></div><button type="button" onClick={onNew} className="flex h-11 items-center gap-2 rounded-lg bg-[#5865F2] px-4 text-sm font-bold text-white"><Plus className="h-4 w-4" /> 새 디자인</button></div>
      {items.length ? (
        <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {items.map((entry, index) => {
            const product = entry.draftProduct || entry.product;
            const pending = entry.product.reviewStatus === "PENDING";
            return (
              <motion.article key={entry.product.id} initial={reduceMotion ? false : { opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: reduceMotion ? 0 : 0.22, delay: reduceMotion ? 0 : Math.min(index, 6) * 0.045 }} whileHover={reduceMotion ? undefined : { y: -3 }} className="overflow-hidden rounded-lg border border-[#353740] bg-[#202126] shadow-lg transition-colors hover:border-[#505465]">
                <button type="button" onClick={() => onEdit(product)} className="block w-full bg-[#15161a] p-4"><MarketplacePreviewCard product={product} compact /></button>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2"><div className="min-w-0"><h3 className="truncate font-black text-white">{product.name}</h3><p className="mt-1 text-sm text-gray-400">{money(product.price)}</p></div><div className="flex flex-col items-end gap-1"><StatusBadge status={entry.product.status} /><StatusBadge status={entry.product.reviewStatus} /></div></div>
                  {entry.product.rejectionNote && <p className="mt-3 rounded-md bg-red-500/10 p-2 text-xs text-red-300">{entry.product.rejectionNote}</p>}
                  <div className="mt-4 grid grid-cols-2 gap-2"><button type="button" onClick={() => onEdit(product)} className="rounded-lg border border-[#464954] py-2 text-sm font-bold text-gray-200">수정</button><button type="button" disabled={pending || busy} onClick={() => onSubmit(entry.product.id)} className="rounded-lg bg-[#5865F2] py-2 text-sm font-bold text-white disabled:bg-gray-700">{pending ? "심사 중" : "심사 요청"}</button></div>
                  <button type="button" disabled={pending || busy} onClick={() => onDelete(entry.product.id)} className="mt-2 flex w-full items-center justify-center gap-2 py-2 text-xs text-red-300 disabled:text-gray-600"><Trash2 className="h-3.5 w-3.5" /> 상품 내리기</button>
                </div>
              </motion.article>
            );
          })}
        </div>
      ) : <div className="mt-8 rounded-lg border border-dashed border-[#3b3e47] py-24 text-center text-gray-500"><Palette className="mx-auto h-8 w-8" /><p className="mt-3">첫 서버 카드 디자인을 만들어보세요.</p></div>}
    </div>
  );
}

function Earnings({ dashboard, onRefresh, onRequest, busy }) {
  if (!dashboard) return <div className="p-16 text-center text-gray-400"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>;
  const { balance = {}, sales = [], settlements = [] } = dashboard;
  const minimum = balance.minimumPayoutAmount || 50000;
  const progress = Math.min(100, (balance.availableAmount / minimum) * 100);
  return (
    <div className="mx-auto max-w-6xl px-4 py-8 text-white sm:px-6">
      <div className="flex items-center justify-between"><div><h2 className="text-2xl font-black">판매 및 정산</h2><p className="mt-1 text-sm text-gray-500">판매 수익은 환불 가능 기간이 지난 뒤 정산 가능 금액으로 이동합니다.</p></div><button type="button" onClick={onRefresh} className="creator-tool-button"><RefreshCw className="h-4 w-4" /></button></div>
      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {[['정산 가능', balance.availableAmount, 'text-emerald-300'], ['보류 중', balance.pendingAmount, 'text-amber-200'], ['정산 처리 중', balance.lockedAmount, 'text-blue-300'], ['누적 판매 수익', balance.lifetimeSalesAmount, 'text-white']].map(([label, value, color]) => <div key={label} className="rounded-lg border border-[#353740] bg-[#202126] p-5"><p className="text-xs font-bold text-gray-500">{label}</p><strong className={`mt-2 block text-2xl ${color}`}>{money(value)}</strong></div>)}
      </div>
      <section className="mt-5 rounded-lg border border-[#353740] bg-[#202126] p-5">
        <div className="flex flex-col gap-5 sm:flex-row sm:items-end sm:justify-between"><div className="flex-1"><div className="flex justify-between text-sm"><strong>최소 정산 금액까지</strong><span className="text-gray-400">{money(balance.availableAmount)} / {money(minimum)}</span></div><div className="mt-3 h-2 overflow-hidden rounded-full bg-black/30"><div className="h-full rounded-full bg-[#5865F2]" style={{ width: `${progress}%` }} /></div><p className="mt-2 text-xs leading-5 text-gray-500">정산 신청 시 현재 정산 가능 금액 전체가 지급 요청으로 접수됩니다.<br />관리자 확인 완료 후 최대 1시간 이내에 등록된 계좌로 이체됩니다.<br />단, 금융기관 점검·장애 또는 계좌정보 오류가 있는 경우 지연될 수 있습니다.</p></div><button type="button" disabled={busy || balance.availableAmount < minimum || balance.lockedAmount > 0} onClick={onRequest} className="h-11 rounded-lg bg-emerald-600 px-5 text-sm font-bold hover:bg-emerald-500 disabled:cursor-not-allowed disabled:bg-gray-700">정산 신청</button></div>
      </section>
      <DataSection title="최근 판매" empty="아직 판매 내역이 없습니다."><table className="creator-table"><thead><tr><th>상품</th><th>구매자</th><th>결제액</th><th>판매 수익</th><th>상태</th><th>결제일</th></tr></thead><tbody>{sales.map((sale) => <tr key={sale.orderId}><td>{sale.productName}</td><td>{sale.buyerName}</td><td>{money(sale.amount)}</td><td>{money(sale.sellerAmount)}</td><td><StatusBadge status={sale.status} /></td><td>{shortDate(sale.paidAt)}</td></tr>)}</tbody></table></DataSection>
      <DataSection title="정산 내역" empty="정산 신청 내역이 없습니다."><table className="creator-table"><thead><tr><th>신청 금액</th><th>계좌</th><th>상태</th><th>신청일</th><th>처리 메모</th></tr></thead><tbody>{settlements.map((row) => <tr key={row.id}><td>{money(row.amount)}</td><td>{row.bankName} ···· {row.accountLast4}</td><td><StatusBadge status={row.status} /></td><td>{shortDate(row.requestedAt)}</td><td>{row.adminNote || '-'}</td></tr>)}</tbody></table></DataSection>
    </div>
  );
}

function DataSection({ title, empty, children }) {
  const bodyRows = children?.props?.children?.[1]?.props?.children;
  const isEmpty = Array.isArray(bodyRows) && bodyRows.length === 0;
  return <section className="mt-7"><h3 className="mb-3 text-lg font-black text-white">{title}</h3>{isEmpty ? <div className="rounded-lg border border-dashed border-[#3b3e47] py-12 text-center text-sm text-gray-500">{empty}</div> : <div className="overflow-x-auto rounded-lg border border-[#353740]">{children}</div>}</section>;
}

function AdminConsole({ onMessage, onBack }) {
  const [data, setData] = useState({ reviews: [], settlements: [], refunds: [] });
  const [tab, setTab] = useState("reviews");
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    const [reviews, settlements, refunds] = await Promise.all([
      api("/api/admin/marketplace/reviews"), api("/api/admin/marketplace/settlements"), api("/api/admin/marketplace/refunds"),
    ]);
    setData({ reviews, settlements, refunds });
  }, []);
  useEffect(() => { load().catch((error) => onMessage(error.message, true)); }, [load, onMessage]);

  const action = async (path, body, confirmText, method = "POST") => {
    if (confirmText && !window.confirm(confirmText)) return;
    setBusy(true);
    try { const result = await api(path, { method, ...(body ? { body: JSON.stringify(body) } : {}) }); onMessage(result.message || "처리했습니다."); await load(); }
    catch (error) { onMessage(error.message, true); }
    finally { setBusy(false); }
  };
  const reason = (label) => window.prompt(`${label} 사유를 입력해주세요.`) || "";

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 text-white sm:px-6">
      <div className="flex items-start justify-between gap-4"><div className="flex min-w-0 items-start gap-3"><button type="button" onClick={onBack} className="creator-tool-button mt-0.5" aria-label="마켓 관리자에서 돌아가기" title="돌아가기"><ArrowLeft className="h-4 w-4" /></button><div><h2 className="flex items-center gap-2 text-2xl font-black"><Shield className="h-6 w-6 text-[#9ea6ff]" /> 마켓 관리자</h2><p className="mt-1 text-sm text-gray-500">서버 카드 심사, 정산 이체 기록, 결제 환불을 처리합니다.</p></div></div><button type="button" onClick={load} className="creator-tool-button"><RefreshCw className="h-4 w-4" /></button></div>
      <div className="mt-6 flex gap-2 overflow-x-auto border-b border-[#33353b]">{[['reviews','서버 카드 심사',data.reviews.length],['settlements','정산',data.settlements.length],['refunds','환불',data.refunds.length]].map(([id,label,count]) => <button key={id} type="button" onClick={() => setTab(id)} className={`shrink-0 border-b-2 px-4 pb-3 text-sm font-bold ${tab === id ? 'border-[#5865F2] text-white' : 'border-transparent text-gray-500'}`}>{label} {count}</button>)}</div>

      {tab === "reviews" && <div className="mt-6 grid gap-5 lg:grid-cols-2">{data.reviews.map((item) => <article key={item.product.id} className="grid gap-4 rounded-lg border border-[#353740] bg-[#202126] p-4 sm:grid-cols-[180px_1fr]"><MarketplacePreviewCard product={item.product} compact /><div><div className="flex justify-between"><div><h3 className="font-black">{item.product.name}</h3><p className="text-xs text-gray-500">{item.seller.displayName} · {money(item.product.price)}</p></div><StatusBadge status={item.reviewStatus} /></div><p className="mt-3 line-clamp-3 whitespace-pre-line text-sm text-gray-300">{item.product.description}</p>{item.rejectionNote && <p className="mt-2 text-xs text-red-300">이전 반려: {item.rejectionNote}</p>}<div className="mt-4 flex flex-wrap gap-2"><button disabled={busy || item.reviewStatus !== 'PENDING'} onClick={() => action(`/api/admin/marketplace/products/${item.product.id}/review`, { action: 'APPROVE' }, '이 상품을 상점에 공개할까요?')} className="admin-ok"><Check className="h-4 w-4" /> 승인</button><button disabled={busy || item.reviewStatus !== 'PENDING'} onClick={() => { const value=reason('상품 반려'); if(value) action(`/api/admin/marketplace/products/${item.product.id}/review`, { action:'REJECT', reason:value }); }} className="admin-no"><X className="h-4 w-4" /> 반려</button><button disabled={busy} onClick={() => action(`/api/admin/marketplace/products/${item.product.id}`, null, '이 상품을 삭제할까요? 판매 이력이 없는 상품은 복구할 수 없습니다.', 'DELETE')} className="admin-no"><Trash2 className="h-4 w-4" /> 삭제</button></div></div></article>)}{!data.reviews.length && <AdminEmpty text="심사할 상품이 없습니다." />}</div>}

      {tab === "settlements" && <AdminTable headers={['판매자','금액','지급 계좌','상태','신청일','처리']}>{data.settlements.map((row) => <tr key={row.id}><td>{row.sellerName}<small>Discord {row.discordId}</small></td><td><strong>{money(row.amount)}</strong></td><td>{row.bankName} {row.accountHolder}<small>{row.accountNumber}</small></td><td><StatusBadge status={row.status} /></td><td>{shortDate(row.requestedAt)}</td><td><div className="flex gap-2">{row.status === 'REQUESTED' && <button disabled={busy} onClick={() => action(`/api/admin/marketplace/settlements/${row.id}`, {action:'PROCESSING',note:'이체 확인 중'})} className="admin-neutral">처리 시작</button>}{['REQUESTED','PROCESSING'].includes(row.status) && <><button disabled={busy} onClick={() => { const ref=window.prompt('은행 이체 거래번호를 입력해주세요.'); if(ref) action(`/api/admin/marketplace/settlements/${row.id}`, {action:'PAID',transferReference:ref}, `${money(row.amount)} 지급을 완료 처리할까요?`); }} className="admin-ok">지급 완료</button><button disabled={busy} onClick={() => { const value=reason('정산 반려'); if(value) action(`/api/admin/marketplace/settlements/${row.id}`, {action:'REJECTED',note:value}); }} className="admin-no">반려</button></>}</div></td></tr>)}</AdminTable>}

      {tab === "refunds" && <AdminTable headers={['주문','구매자','상품/금액','사유','상태','처리']}>{data.refunds.map((row) => <tr key={row.id}><td>{row.orderId}</td><td>{row.buyerName}<small>{row.buyerDiscordId}</small></td><td>{row.productName}<small>{money(row.amount)} · {row.sellerName}</small></td><td className="max-w-xs whitespace-normal">{row.reason}</td><td><StatusBadge status={row.status} /></td><td>{row.status === 'REQUESTED' && <div className="flex gap-2"><button disabled={busy} onClick={() => action(`/api/admin/marketplace/refunds/${row.id}`, {action:'APPROVE'}, `토스페이먼츠에서 ${money(row.amount)}를 실제로 취소합니다. 계속할까요?`)} className="admin-ok">환불 승인</button><button disabled={busy} onClick={() => { const value=reason('환불 반려'); if(value) action(`/api/admin/marketplace/refunds/${row.id}`, {action:'REJECT',note:value}); }} className="admin-no">반려</button></div>}</td></tr>)}</AdminTable>}
    </div>
  );
}

function AdminTable({ headers, children }) {
  return <div className="mt-6 overflow-x-auto rounded-lg border border-[#353740]"><table className="creator-table min-w-[900px]"><thead><tr>{headers.map((header) => <th key={header}>{header}</th>)}</tr></thead><tbody>{children}</tbody></table></div>;
}
function AdminEmpty({ text }) { return <div className="col-span-full rounded-lg border border-dashed border-[#3b3e47] py-20 text-center text-gray-500">{text}</div>; }

export default function ServerCardStudio({ embedded = false }) {
  const reduceMotion = useReducedMotion();
  const [creator, setCreator] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loginRequired, setLoginRequired] = useState(false);
  const [active, setActive] = useState("design");
  const [products, setProducts] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [editorProduct, setEditorProduct] = useState(() => ({ ...EMPTY_CREATOR_PRODUCT }));
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState({ message: "", error: false });
  const editorHydratedRef = useRef(false);

  const show = useCallback((message, error = false) => {
    setNotice({ message, error });
    window.setTimeout(() => setNotice({ message: "", error: false }), 4500);
  }, []);

  const loadCreator = useCallback(async () => {
    try { setCreator(await api("/api/creator/me")); setLoginRequired(false); }
    catch (error) { if (error.message.includes("로그인")) setLoginRequired(true); else show(error.message, true); }
    finally { setLoading(false); }
  }, [show]);
  const loadProducts = useCallback(async () => {
    const items = await api("/api/creator/products");
    setProducts(items);
    if (!editorHydratedRef.current) {
      editorHydratedRef.current = true;
      const latestDraft = items.find((entry) => entry?.draftProduct && entry?.product?.reviewStatus !== "PENDING");
      if (latestDraft) setEditorProduct(latestDraft.draftProduct);
    }
    return items;
  }, []);
  const loadDashboard = useCallback(async () => { setDashboard(await api("/api/creator/dashboard")); }, []);

  useEffect(() => { loadCreator(); }, [loadCreator]);
  useEffect(() => {
    if (creator?.seller?.status === "APPROVED" && creator.seller.termsVersion === creator?.settings?.sellerTermsVersion) {
      loadProducts().catch((error) => show(error.message, true));
      loadDashboard().catch(() => {});
    }
  }, [creator, loadProducts, loadDashboard, show]);

  const uploadAsset = async (file) => {
    const form = new FormData(); form.append("file", file);
    try { return await api("/api/creator/assets", { method: "POST", body: form }); }
    catch (error) { show(error.message, true); throw error; }
  };
  const saveProduct = async (product) => {
    setBusy(true);
    try {
      const result = await api("/api/creator/products", { method: "POST", body: JSON.stringify({ product }) });
      if (result.product) {
        editorHydratedRef.current = true;
        setEditorProduct(result.product);
      }
      show(result.message);
      await loadProducts();
      return result.product;
    } catch (error) { show(error.message, true); return null; }
    finally { setBusy(false); }
  };
  const submitProduct = async (id) => {
    setBusy(true);
    try { const result = await api(`/api/creator/products/${id}/submit`, { method: "POST" }); show(result.message); await loadProducts(); setActive("products"); }
    catch (error) { show(error.message, true); }
    finally { setBusy(false); }
  };
  const deleteProduct = async (id) => {
    if (!window.confirm("이 상품을 상점에서 내릴까요?")) return;
    setBusy(true);
    try { const result = await api(`/api/creator/products/${id}`, { method: "DELETE" }); show(result.message); await loadProducts(); }
    catch (error) { show(error.message, true); }
    finally { setBusy(false); }
  };
  const requestSettlement = async () => {
    if (!window.confirm("현재 정산 가능 금액 전체를 등록된 계좌로 지급 요청할까요?\n\n관리자 확인 완료 후 최대 1시간 이내에 이체됩니다.\n단, 금융기관 점검·장애 또는 계좌정보 오류가 있는 경우 지연될 수 있습니다.")) return;
    setBusy(true);
    try { const result = await api("/api/creator/settlements", { method: "POST" }); show(`${money(result.amount)} 정산 신청이 접수되었습니다.`); await loadDashboard(); }
    catch (error) { show(error.message, true); }
    finally { setBusy(false); }
  };

  if (loading) return <div className={`${embedded ? "py-24" : "min-h-screen bg-[#111216] pt-32"} text-center text-gray-400`}><Loader2 className="mx-auto h-7 w-7 animate-spin" /></div>;
  if (loginRequired) return <div className={`flex flex-col items-center justify-center px-4 py-24 text-center text-white ${embedded ? "" : "min-h-screen bg-[#111216]"}`}><Store className="h-10 w-10 text-[#8f98ff]" /><h1 className="mt-5 text-2xl font-black">로그인이 필요합니다</h1><p className="mt-2 text-gray-400">서버 카드 디자인과 판매 관리는 Discord 로그인 후 이용할 수 있어요.</p><a href="https://disparty.com/login" className="mt-6 rounded-lg bg-[#5865F2] px-6 py-3 font-bold">Discord로 로그인</a></div>;

  const sellerTermsAreCurrent = creator?.seller?.termsVersion === creator?.settings?.sellerTermsVersion;
  if (!creator?.seller || creator.seller.status !== "APPROVED" || !sellerTermsAreCurrent) {
    if (active === "admin" && creator?.isAdmin) return <div className={embedded ? "bg-transparent" : "min-h-screen bg-[#111216]"}><Notice {...notice} /><AdminConsole onMessage={show} onBack={() => setActive("design")} /></div>;
    return <SellerApplication embedded={embedded} seller={creator?.seller} settings={creator?.settings || {}} isAdmin={creator?.isAdmin} onAdmin={() => setActive("admin")} onSaved={(seller) => { setCreator((current) => ({ ...current, seller })); show("판매 정보 등록이 완료되었습니다."); }} />;
  }

  const tabs = [
    { id: "design", label: "디자인", icon: Palette },
    { id: "products", label: "내 상품", icon: Package },
    { id: "earnings", label: "판매·정산", icon: Banknote },
    { id: "settings", label: "크리에이터 설정", icon: Settings },
    ...(creator.isAdmin ? [{ id: "admin", label: "관리자", icon: Shield }] : []),
  ];

  return (
    <div className={`${embedded ? "bg-transparent" : "min-h-screen bg-[#111216]"} text-white`}>
      <Notice {...notice} />
      <header className={`${embedded ? "relative" : "sticky top-0"} z-[900] border-b border-[#30323a] bg-[#17181c]/95 backdrop-blur`}>
        <div className="mx-auto flex max-w-7xl items-center justify-between gap-4 px-4 sm:px-6">
          <div className="hidden py-4 sm:block"><p className="text-sm font-black">서버 카드 스튜디오</p><p className="text-[11px] text-gray-500">{creator.seller.displayName}</p></div>
          <nav className="flex flex-1 justify-center gap-1 overflow-x-auto">
            {tabs.map((tab) => { const Icon=tab.icon; return <motion.button key={tab.id} type="button" whileTap={reduceMotion ? undefined : { scale: 0.96 }} onClick={() => { setActive(tab.id); if(tab.id==='earnings') loadDashboard(); }} className={`relative flex h-16 shrink-0 items-center gap-2 px-3 text-sm font-bold transition-colors ${active === tab.id ? 'text-white' : 'text-gray-500 hover:text-gray-200'}`}><Icon className={`h-4 w-4 transition-colors ${active === tab.id ? 'text-[#9ea6ff]' : ''}`} />{tab.label}{active === tab.id && <motion.span layoutId="creator-active-tab" className="absolute inset-x-2 bottom-0 h-0.5 rounded-full bg-[#7c86ff]" transition={reduceMotion ? { duration: 0 } : { type: "spring", stiffness: 420, damping: 34 }} />}</motion.button>; })}
          </nav>
          <div className="hidden items-center gap-2 py-4 text-xs text-gray-400 lg:flex"><BadgeCheck className="h-4 w-4 text-emerald-400" /> 판매 등록 완료</div>
        </div>
      </header>

      <AnimatePresence mode="wait" initial={false}>
        <motion.main
          key={active}
          initial={reduceMotion ? false : { opacity: 0, x: 12 }}
          animate={{ opacity: 1, x: 0 }}
          exit={reduceMotion ? { opacity: 1 } : { opacity: 0, x: -8 }}
          transition={{ duration: reduceMotion ? 0 : 0.2, ease: "easeOut" }}
        >
          {active === "design" && <ServerCardDesigner initialProduct={editorProduct} onSave={saveProduct} onSubmit={submitProduct} onUploadAsset={uploadAsset} busy={busy} />}
          {active === "products" && <ProductManager items={products} busy={busy} onNew={() => { editorHydratedRef.current = true; setEditorProduct(JSON.parse(JSON.stringify(EMPTY_CREATOR_PRODUCT))); setActive('design'); }} onEdit={(product) => { editorHydratedRef.current = true; setEditorProduct(product); setActive('design'); }} onDelete={deleteProduct} onSubmit={submitProduct} />}
          {active === "earnings" && <Earnings dashboard={dashboard} busy={busy} onRefresh={loadDashboard} onRequest={requestSettlement} />}
          {active === "settings" && <SellerApplication embedded seller={creator.seller} settings={creator.settings || {}} isAdmin={creator.isAdmin} onAdmin={() => setActive("admin")} onSaved={(seller) => { setCreator((current) => ({ ...current, seller })); show("판매 정보를 저장했습니다."); }} />}
          {active === "admin" && creator.isAdmin && <AdminConsole onMessage={show} onBack={() => setActive("products")} />}
        </motion.main>
      </AnimatePresence>
    </div>
  );
}
